import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useT } from "@/lib/i18n";
import PageHeader from "@/components/PageHeader";
import RowActions from "@/components/RowActions";
import ConfirmDelete from "@/components/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, UserCircle2, CheckCircle2, Circle, ChevronRight, BellRing } from "lucide-react";
import BrainTypeField from "@/components/BrainTypeField";
import ClientTypeField, { useClientTypeLabel } from "@/components/ClientTypeField";
import ClientRequestBadges from "@/components/ClientRequestBadges";
import { rowColorForUser } from "@/lib/rowColor";
import { toast } from "sonner";

const emptyForm = { name: "", email: "", password: "", brain_type: null, client_type: "prospect" };

export default function CoachUsers() {
  const navigate = useNavigate();
  const { t } = useT();
  const clientTypeLabel = useClientTypeLabel();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setError("");
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name,
      email: u.email,
      password: "",
      brain_type: u.brain_type ?? null,
      client_type: u.client_type || "prospect",
    });
    setError("");
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, email: form.email };
        if (form.password) payload.password = form.password;
        if (form.brain_type != null) payload.brain_type = form.brain_type;
        if (form.client_type) payload.client_type = form.client_type;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Client updated");
      } else {
        const payload = { ...form };
        if (payload.brain_type == null) delete payload.brain_type;
        await api.post("/users", payload);
        toast.success("Client added");
      }
      setOpen(false);
      fetchData();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/users/${confirmTarget.id}`);
      toast.success("Client deleted");
      setConfirmTarget(null);
      fetchData();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  // Quick handler for "Mark handled" on a row's pending requests
  const markHandled = async (e, userId) => {
    e.stopPropagation();
    try {
      await api.post(`/users/${userId}/coaching-requests/resolve-all`);
      toast.success("Request marked handled");
      fetchData();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // Pre-compute alert summary for the top banner
  const clientsWantingBusiness = users.filter(
    (u) => u.pending_requests && u.pending_requests.length > 0
  );
  const totalRequests = clientsWantingBusiness.reduce(
    (n, u) => n + u.pending_requests.length,
    0
  );

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-8 sm:py-12 max-w-7xl" data-testid="coach-users-page">
      <PageHeader
        overline={t("overline.coach")}
        title={t("coach_users.title")}
        description={t("coach_users.description")}
        action={
          <Button
            onClick={openCreate}
            data-testid="create-user-button"
            className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white"
          >
            <Plus strokeWidth={1.5} size={18} className="mr-2" />
            {t("coach_users.add_client")}
          </Button>
        }
      />

      {clientsWantingBusiness.length > 0 && (
        <div
          className="mb-8 bg-[#F5EAE2] border border-[#E5D2C2] rounded-2xl p-5 sm:p-6 flex items-start gap-4"
          data-testid="pending-requests-banner"
        >
          <div className="h-11 w-11 rounded-xl bg-white flex items-center justify-center shrink-0 border border-[#E5D2C2]">
            <BellRing strokeWidth={1.5} className="text-[#B37A5F]" size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-lg sm:text-xl text-[#1A1D1A] tracking-tight font-semibold">
              {clientsWantingBusiness.length === 1
                ? t("coach_users.banner.one")
                : t("coach_users.banner.many", { count: clientsWantingBusiness.length })}
              {totalRequests > clientsWantingBusiness.length &&
                t("coach_users.banner.total_suffix", { total: totalRequests })}
            </h3>
            <p className="text-sm text-[#5C635A] mt-1 leading-relaxed">
              {clientsWantingBusiness
                .slice(0, 5)
                .map((u) => u.name)
                .join(", ")}
              {clientsWantingBusiness.length > 5
                ? `, ${t("coach_users.banner.more", { n: clientsWantingBusiness.length - 5 })}`
                : ""}
              . {t("coach_users.banner.description")}
            </p>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-[#E5E2DC] sm:max-w-lg" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight text-[#1A1D1A] font-semibold">
              {editing ? t("coach_users.dialog.edit_title") : t("coach_users.dialog.add_title")}
            </DialogTitle>
            <DialogDescription className="text-[#5C635A]">
              {editing ? t("coach_users.dialog.edit_desc") : t("coach_users.dialog.add_desc")}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <Field label={t("coach_users.field.name")} testid="user-name-input" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
            <Field label={t("coach_users.field.email")} type="email" testid="user-email-input" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
            <Field
              label={editing ? t("coach_users.field.new_password_optional") : t("coach_users.field.initial_password")}
              type="password"
              testid="user-password-input"
              value={form.password}
              onChange={(v) => setForm({ ...form, password: v })}
              required={!editing}
              placeholder={editing ? t("coach_users.field.password_placeholder_edit") : ""}
            />
            <BrainTypeField
              value={form.brain_type}
              onChange={(v) => setForm({ ...form, brain_type: v })}
              testid="user-brain-type-input"
              label={t("coach_users.field.brain_type")}
            />
            <ClientTypeField
              value={form.client_type}
              onChange={(v) => setForm({ ...form, client_type: v })}
              testid="user-client-type-input"
            />
            {error && (
              <p
                className="text-sm text-[#9C6A53] bg-[#9C6A53]/10 border border-[#9C6A53]/20 rounded-lg px-4 py-3"
                data-testid="user-error"
              >
                {error}
              </p>
            )}
            <DialogFooter>
              <Button
                type="submit"
                disabled={saving}
                data-testid="user-submit-button"
                className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white"
              >
                {saving ? t("common.saving") : editing ? t("common.save_changes") : t("coach_users.add_client")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDelete
        open={!!confirmTarget}
        onOpenChange={(v) => !v && setConfirmTarget(null)}
        title={t("coach_users.delete_title")}
        description={
          confirmTarget ? t("coach_users.delete_description", { name: confirmTarget.name }) : ""
        }
        onConfirm={handleDelete}
        loading={deleting}
        testIdPrefix="user-delete"
      />

      <div className="bg-white border border-[#E5E2DC] rounded-2xl overflow-hidden" data-testid="users-list">
        {/* Desktop / tablet header — hidden on mobile, replaced by stacked cards */}
        <div className="hidden md:grid grid-cols-12 px-6 py-4 text-xs uppercase tracking-[0.2em] text-[#8A9188] font-bold border-b border-[#E5E2DC]">
          <div className="col-span-3">{t("coach_users.col.client")}</div>
          <div className="col-span-3">{t("coach_users.col.email")}</div>
          <div className="col-span-1 text-center">{t("coach_users.col.brain")}</div>
          <div className="col-span-2">{t("coach_users.col.type")}</div>
          <div className="col-span-2">{t("coach_users.col.onboarding")}</div>
          <div className="col-span-1 text-right">{t("coach_users.col.added")}</div>
        </div>
        {loading ? (
          <div className="px-6 py-10 text-sm text-[#8A9188]">{t("coach_users.loading")}</div>
        ) : users.length === 0 ? (
          <div className="px-6 py-16 text-center" data-testid="users-empty">
            <UserCircle2 strokeWidth={1.5} className="mx-auto text-[#B37A5F] mb-3" size={32} />
            <p className="text-[#5C635A]">{t("coach_users.empty")}</p>
          </div>
        ) : (
          users.map((u) => {
            const bg = rowColorForUser(u);
            return (
              <div
                key={u.id}
                onClick={() => navigate(`/details/user/${u.id}`)}
                style={bg ? { backgroundColor: bg } : undefined}
                className="border-b last:border-0 border-[#E5E2DC] hover:brightness-95 transition-[filter] cursor-pointer group"
                data-testid={`user-row-${u.id}`}
              >
                {/* MOBILE: stacked card layout */}
                <div className="md:hidden px-4 py-4 flex flex-col gap-2.5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-[#1A1D1A] truncate flex items-center gap-1.5">
                        {u.name}
                        <ChevronRight strokeWidth={1.5} size={14} className="text-[#B37A5F] shrink-0" />
                      </div>
                      <div className="text-xs text-[#5C635A] truncate mt-0.5">{u.email}</div>
                    </div>
                    <div
                      className="font-heading text-2xl text-[#1A1D1A] font-semibold leading-none shrink-0"
                      data-testid={`user-brain-${u.id}`}
                      title="Brain type"
                    >
                      {u.brain_type ?? "—"}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      data-testid={`user-client-type-${u.id}`}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] bg-[#F2F0EA] text-[#1A1D1A] border border-[#E5E2DC]"
                    >
                      {clientTypeLabel(u.client_type)}
                    </span>
                    {u.onboarding_completed ? (
                      <span className="inline-flex items-center gap-1 text-[#4A5D4E] text-[11px] font-medium">
                        <CheckCircle2 strokeWidth={1.5} size={12} /> {t("coach_users.onboarded")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[#8A9188] text-[11px]">
                        <Circle strokeWidth={1.5} size={12} /> {t("coach_users.onboarding_pending")}
                      </span>
                    )}
                    <span className="text-[10px] text-[#8A9188] uppercase tracking-wider ml-auto">
                      {new Date(u.created_at).toLocaleDateString()}
                    </span>
                    <RowActions
                      onEdit={() => openEdit(u)}
                      onDelete={() => setConfirmTarget(u)}
                      testIdPrefix={`user-${u.id}`}
                    />
                  </div>
                  {u.pending_requests && u.pending_requests.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                      <ClientRequestBadges
                        pendingRequests={u.pending_requests}
                        size="sm"
                        testid={`user-request-badges-${u.id}-m`}
                      />
                      <button
                        type="button"
                        onClick={(e) => markHandled(e, u.id)}
                        data-testid={`user-mark-handled-${u.id}-m`}
                        className="text-[10px] text-[#8A9188] hover:text-[#1A1D1A] font-bold uppercase tracking-[0.15em] hover:underline"
                      >
                        {t("coach_users.mark_handled")}
                      </button>
                    </div>
                  )}
                </div>

                {/* DESKTOP / TABLET: original 12-col grid */}
                <div className="hidden md:grid grid-cols-12 px-6 py-5 items-center">
                  <div className="col-span-3 font-medium text-[#1A1D1A] flex flex-col gap-1.5 min-w-0">
                    <span className="flex items-center gap-2">
                      {u.name}
                      <ChevronRight strokeWidth={1.5} size={14} className="text-[#B37A5F] opacity-0 group-hover:opacity-100 transition-opacity" />
                    </span>
                    {u.pending_requests && u.pending_requests.length > 0 && (
                      <div className="flex flex-col gap-1">
                        <ClientRequestBadges
                          pendingRequests={u.pending_requests}
                          size="sm"
                          testid={`user-request-badges-${u.id}`}
                        />
                        <button
                          type="button"
                          onClick={(e) => markHandled(e, u.id)}
                          data-testid={`user-mark-handled-${u.id}`}
                          className="text-[10px] text-[#8A9188] hover:text-[#1A1D1A] font-bold uppercase tracking-[0.15em] self-start hover:underline"
                        >
                          {t("coach_users.mark_handled")}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="col-span-3 text-sm text-[#5C635A] truncate">{u.email}</div>
                  <div className="col-span-1 text-center font-heading text-lg text-[#1A1D1A] font-semibold">
                    {u.brain_type ?? "—"}
                  </div>
                  <div className="col-span-2">
                    <span
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] bg-[#F2F0EA] text-[#1A1D1A] border border-[#E5E2DC]"
                    >
                      {clientTypeLabel(u.client_type)}
                    </span>
                  </div>
                  <div className="col-span-2">
                    {u.onboarding_completed ? (
                      <span className="inline-flex items-center gap-1.5 text-[#4A5D4E] text-xs font-medium">
                        <CheckCircle2 strokeWidth={1.5} size={14} /> {t("coach_users.onboarding_complete")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-[#8A9188] text-xs">
                        <Circle strokeWidth={1.5} size={14} /> {t("coach_users.onboarding_pending")}
                      </span>
                    )}
                  </div>
                  <div className="col-span-1 text-right text-xs text-[#8A9188] uppercase tracking-wider flex items-center justify-end gap-2">
                    <span>{new Date(u.created_at).toLocaleDateString()}</span>
                    <RowActions
                      onEdit={() => openEdit(u)}
                      onDelete={() => setConfirmTarget(u)}
                      testIdPrefix={`user-${u.id}`}
                    />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1D1A]">{label}</Label>
      <Input
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="h-11 rounded-lg bg-white border-[#E5E2DC] focus-visible:ring-1 focus-visible:ring-[#4A5D4E] focus-visible:border-[#4A5D4E]"
      />
    </div>
  );
}
